/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import { isOfflineDraftsEnabled, normalizePeriodKey } from "./countDraftConstants";
import {
  finalSubmissionCompositeId,
  getFinalSubmissionForScreen,
  getCountDraftInitError
} from "./countDraftDb";
import {
  saveAndAttemptFinalSubmission,
  processFinalSubmissionQueue,
  bindFinalSubmissionConnectivity,
  subscribeFinalSubmissionSync
} from "./finalSubmissionSync";
import axiosInstance from "../config/axios";

const STATUS_COPY = {
  ready: {
    variant: "ready",
    badgeLabel: "Ready to submit",
    headline: "Ready to submit",
    detail: "When you are finished reviewing, submit to your manager."
  },
  saving: {
    variant: "syncing",
    badgeLabel: "Saving pending submission...",
    headline: "Saving pending submission...",
    detail: "Saving your completed count on this device before sending."
  },
  pending: {
    variant: "waiting",
    badgeLabel: "Submission pending",
    headline: "Submission pending",
    detail:
      "Your count has been saved on this device and will be sent automatically when the connection returns."
  },
  syncing: {
    variant: "syncing",
    badgeLabel: "Sending to manager...",
    headline: "Sending count to manager...",
    detail: "Please keep this page open if you can until sending finishes."
  },
  submitted: {
    variant: "synced",
    badgeLabel: "Submitted to manager",
    headline: "Submitted to manager",
    detail: "Your count has been successfully sent to your manager."
  },
  failed_connection: {
    variant: "waiting",
    badgeLabel: "Submission pending",
    headline: "Submission pending",
    detail:
      "Your count is saved on this device. We will keep trying to send it when the connection improves."
  },
  stale: {
    variant: "error",
    badgeLabel: "New counting period started",
    headline: "New counting period started",
    detail:
      "This count belongs to an older counting period and cannot be submitted. Please refresh and complete the current period count."
  },
  failed: {
    variant: "error",
    badgeLabel: "Submission could not be sent",
    headline: "Submission could not be sent",
    detail:
      "Your count could not be sent. Please check the message below, then try again or contact the developer if the issue continues."
  }
};

function uiFromRow(row) {
  if (!row) return null;
  if (row.status === "pending") return { ...STATUS_COPY.pending };
  if (row.status === "syncing") return { ...STATUS_COPY.syncing };
  if (row.status === "stale") return { ...STATUS_COPY.stale };
  if (row.status === "failed") {
    return {
      ...STATUS_COPY.failed,
      detail: row.lastError || STATUS_COPY.failed_connection.detail
    };
  }
  if (row.status === "submitted") return { ...STATUS_COPY.submitted };
  return { ...STATUS_COPY.pending };
}

export function useFinalSubmissionQueue({
  user,
  selectedSiteId,
  countingPeriodStartedAt,
  countingPeriodFetched,
  processingEnabled = true
}) {
  const [submissionUi, setSubmissionUi] = useState(null);
  const [pendingRow, setPendingRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const boundRef = useRef(false);
  const clientSubmissionIdRef = useRef(null);
  const refreshRequestRef = useRef(0);

  const periodKey =
    countingPeriodFetched && user && user.tenantId && selectedSiteId
      ? normalizePeriodKey(countingPeriodStartedAt)
      : null;

  const enabled =
    isOfflineDraftsEnabled() &&
    user &&
    user.tenantId &&
    user.id &&
    selectedSiteId &&
    periodKey;
  const screenSubmissionId = enabled
    ? finalSubmissionCompositeId(user.tenantId, user.id, selectedSiteId, periodKey)
    : null;

  const refresh = useCallback(async () => {
    const requestId = ++refreshRequestRef.current;
    if (!enabled) {
      setPendingRow(null);
      setSubmissionUi(null);
      return;
    }
    const row = await getFinalSubmissionForScreen(
      user.tenantId,
      user.id,
      selectedSiteId,
      periodKey
    );
    if (requestId !== refreshRequestRef.current) return;
    setPendingRow(row || null);
    if (row && (row.status === "pending" || row.status === "syncing" || row.status === "failed" || row.status === "stale")) {
      setSubmissionUi(uiFromRow(row));
      if (row.clientSubmissionId) clientSubmissionIdRef.current = row.clientSubmissionId;
    } else if (!busy) {
      setSubmissionUi(null);
    }
  }, [enabled, user, selectedSiteId, periodKey, busy]);

  const runQueue = useCallback(async () => {
    if (!processingEnabled) return;
    if (!user || !user.tenantId || !user.id) return;
    await processFinalSubmissionQueue(axiosInstance, {
      tenantId: user.tenantId,
      userId: user.id,
      siteId: selectedSiteId,
      currentPeriodKey: periodKey
    });
    await refresh();
  }, [processingEnabled, user, selectedSiteId, periodKey, refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return;
    if (!boundRef.current) {
      bindFinalSubmissionConnectivity(() => {
        void runQueue();
      });
      boundRef.current = true;
    }
    const unsub = subscribeFinalSubmissionSync((event) => {
      if (
        event &&
        event.type === "submitted" &&
        event.row &&
        event.row.id === screenSubmissionId
      ) {
        // Invalidate older queued/syncing reads so they cannot restore stale pending UI.
        refreshRequestRef.current += 1;
        setPendingRow(null);
        setSubmissionUi(null);
        clientSubmissionIdRef.current = null;
      }
      void refresh();
    });
    const t = setInterval(() => {
      if (typeof navigator !== "undefined" && navigator.onLine) {
        void runQueue();
      } else {
        void refresh();
      }
    }, 8000);
    void runQueue();
    return () => {
      unsub();
      clearInterval(t);
    };
  }, [enabled, runQueue, refresh, screenSubmissionId]);

  /**
   * Persist final submission locally first, then attempt network submit.
   * Returns { outcome, response?, message?, localOnly? }
   */
  const submitFinalToManager = useCallback(
    async ({ personName, generalNotes, recalledBatchId, counts }) => {
      if (!user || !user.tenantId || !user.id || !selectedSiteId || !periodKey) {
        return { outcome: "failed", message: "Missing site or counting period context." };
      }
      if (getCountDraftInitError()) {
        return {
          outcome: "failed",
          message:
            "This device could not save a pending submission offline. Please wait for a connection and try again."
        };
      }

      setBusy(true);
      setSubmissionUi({ ...STATUS_COPY.saving });

      try {
        const id = finalSubmissionCompositeId(user.tenantId, user.id, selectedSiteId, periodKey);
        const existing = await getFinalSubmissionForScreen(
          user.tenantId,
          user.id,
          selectedSiteId,
          periodKey
        );
        const requestedRecalledBatchId = recalledBatchId || null;
        const existingRecalledBatchId =
          existing && existing.payload ? existing.payload.recalledBatchId || null : null;
        const existingMatchesAttempt =
          Boolean(existing) && existingRecalledBatchId === requestedRecalledBatchId;

        let clientSubmissionId =
          existingMatchesAttempt && existing.clientSubmissionId
            ? existing.clientSubmissionId
            : null;
        if (!clientSubmissionId) {
          const canReuseRef = !existing && !recalledBatchId;
          clientSubmissionId =
            (canReuseRef && clientSubmissionIdRef.current) ||
            (typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `sub-${Date.now()}-${Math.random().toString(16).slice(2)}`);
        }
        const record = {
          id,
          clientSubmissionId,
          tenantId: user.tenantId,
          userId: user.id,
          siteId: selectedSiteId,
          currentPeriodKey: periodKey,
          countingPeriodStartedAt,
          payload: {
            personName,
            generalNotes,
            recalledBatchId: recalledBatchId || null,
            counts
          },
          createdAt:
            (existingMatchesAttempt && existing.createdAt) || new Date().toISOString(),
          attemptCount:
            (existingMatchesAttempt && existing.attemptCount) || 0,
          nextRetryAt: null
        };
        clientSubmissionIdRef.current = record.clientSubmissionId;

        const result = await saveAndAttemptFinalSubmission(axiosInstance, record, {});
        if (result.outcome === "submitted") {
          setSubmissionUi({ ...STATUS_COPY.submitted });
          setPendingRow(null);
          clientSubmissionIdRef.current = null;
          return result;
        }
        if (result.outcome === "pending") {
          setSubmissionUi({ ...STATUS_COPY.pending });
          await refresh();
          return result;
        }
        if (result.outcome === "stale") {
          setSubmissionUi({ ...STATUS_COPY.stale });
          await refresh();
          return result;
        }
        setSubmissionUi({
          ...STATUS_COPY.failed,
          detail: result.message || STATUS_COPY.failed_connection.detail
        });
        await refresh();
        return result;
      } catch (e) {
        setSubmissionUi({
          ...STATUS_COPY.failed_connection,
          detail:
            (e && e.message) ||
            STATUS_COPY.failed_connection.detail
        });
        return { outcome: "failed", message: (e && e.message) || "Could not save pending submission" };
      } finally {
        setBusy(false);
      }
    },
    [user, selectedSiteId, periodKey, countingPeriodStartedAt, refresh]
  );

  const hasBlockingPending =
    pendingRow &&
    (pendingRow.status === "pending" ||
      pendingRow.status === "syncing" ||
      pendingRow.status === "submitted");

  return {
    enabled,
    submissionUi,
    pendingRow,
    busy,
    submitFinalToManager,
    hasBlockingPending,
    refreshFinalSubmission: refresh,
    retryFinalSubmission: runQueue,
    statusCopy: STATUS_COPY
  };
}
